import { createRow } from '../../ExpirationRowsEditor/util';

// coverage key on the extraction result → { display label, insurance_policy_types.name }
const COVERAGES = {
  generalLiability: { label: 'General Liability', policyTypeName: 'general' },
  autoLiability: { label: 'Auto Liability', policyTypeName: 'auto' },
  umbrellaExcess: { label: 'Umbrella / Excess Liability', policyTypeName: 'umbrella_excess' },
  workersCompensation: { label: 'Workers Compensation', policyTypeName: 'workers_comp' }
};

const policyNumberNote = policyNumber => (policyNumber ? `Policy # ${policyNumber}` : '');

// a row linked to a policy type takes its name from the type, so the label stays empty
const coverageRow = ({ name, policyTypeId, policy }) => createRow({
  label: policyTypeId ? '' : name,
  date: policy.expiresAt,
  policyTypeId,
  note: policyNumberNote(policy.policyNumber)
});

const findPolicyTypeIdByName = (policyTypes, name) => policyTypes
  .find(type => type.name === name)?.id ?? null;

const findPolicyTypeIdByDisplayName = (policyTypes, displayName) => {
  if (!displayName) return null;

  const needle = displayName.trim().toLowerCase();

  return policyTypes.find(type => type.displayName?.trim().toLowerCase() === needle)?.id ?? null;
};

/**
 * Flattens one normalized extraction result (camelCased `gpt_requests.result[n]`) into editable
 * line items. ACORD forms yield one row per coverage found, pre-linked to the company's matching
 * policy type with the policy number in the note; anything else yields a single row named after the
 * requested document type (so a later document of that type can supersede it) and carrying the
 * extractor's source note.
 * Unrequested documents found in the same file are appended when they carry a date.
 */
export const buildRowsFromResult = (result, policyTypes = []) => {
  if (!result) return [];

  const rows = [];

  if (result.isAcordForm) {
    Object.entries(COVERAGES).forEach(([key, coverage]) => {
      const policy = result[key];
      if (!policy || typeof policy !== 'object') return;

      rows.push(coverageRow({
        name: coverage.label,
        policyTypeId: findPolicyTypeIdByName(policyTypes, coverage.policyTypeName),
        policy
      }));
    });

    (Array.isArray(result.other) ? result.other : []).forEach(policy => {
      rows.push(coverageRow({
        name: policy.policyType || 'Other',
        policyTypeId: findPolicyTypeIdByDisplayName(policyTypes, policy.policyType),
        policy
      }));
    });
  } else if (result.found) {
    rows.push(createRow({
      label: result.requestedTypeDisplayName || result.label || 'Expiration Date',
      date: result.expirationDate,
      note: result.note
    }));
  }

  (result.unrequestedSegments || []).forEach(segment => {
    if (!segment.expirationDate) return;

    rows.push(createRow({
      label: segment.label || 'Additional Document',
      date: segment.expirationDate,
      note: segment.note
    }));
  });

  return rows;
};

export const findResultForDocument = (gptRequest, documentId) => (gptRequest?.result || [])
  .find(result => result.documentId === documentId) || null;
